/**
 * Agents Chat Layout Prototype
 * Cursor-style chats: history in left sidebar, chat in editor tabs or agent panel.
 *
 * Access: http://flownotes.localhost/?test=agents-layout
 *
 * Bet: docs/product/bets/2026-05-29-agents-chat-layout.md
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels'
import {
  ArrowLeft,
  Bot,
  Check,
  FileText,
  Inbox,
  MessageSquare,
  MoreVertical,
  PanelRight,
  Pin,
  Plus,
  Search,
  X,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ContentEditableChatInput } from '@/components/ui/contenteditable-chat-input'
import type { ChatImage, DocumentReference } from '@/components/layout/chat-pane.types'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MOCK_CONVERSATIONS,
  THREAD_KIND_META,
  formatConversationDate,
  getConversationTitle,
  groupConversationsByDay,
  type MockConversation,
} from './test-agents-layout/mock-conversations'
import {
  useChatSurfaceRegistry,
  isModClick,
  type EditorChatTab,
  type OpenConversationOptions,
} from './test-agents-layout/use-chat-surface-registry'
import { ChatFirstHome } from '@/components/layout/chat-first-home'

const MOCK_DOC_TAB = {
  id: 'doc-tab-1',
  title: '2026-05-29-global-home-polish.md',
}

type QaItem = {
  id: string
  label: string
  done: boolean
}

const INITIAL_QA: QaItem[] = [
  { id: 'search', label: 'Chats sidebar lists conversations (agent chats only); search filters the list', done: false },
  { id: 'toggle', label: 'Open chats in editor toggle changes default click behavior', done: false },
  { id: 'editor-tab', label: 'Chat opens as editor tab alongside the mock document tab', done: false },
  { id: 'move-draft', label: 'Move to agent panel preserves typed draft text', done: false },
  { id: 'no-dup', label: 'Re-clicking a conversation never creates two surfaces for the same id', done: false },
  { id: 'no-double-new', label: 'New chat twice focuses the first empty chat (no two welcome screens)', done: false },
  { id: 'dual', label: 'Two different conversations can be open in editor tab + agent panel', done: false },
  { id: 'mod-click', label: 'Cmd/Ctrl+click in Chats list opens a new editor tab (does not replace)', done: false },
  { id: 'pin', label: 'Double-click or context menu can pin a chat tab; pinned tab not replaced on normal open', done: false },
  { id: 'right-click', label: 'Right-click chat in list shows context menu (open in editor/panel, rename)', done: false },
  { id: 'rename', label: 'Double-click chat title in sidebar renames it inline', done: false },
  { id: 'cmd-n', label: 'Cmd/Ctrl+N creates a new chat', done: false },
  { id: 'doc-context', label: 'New chat shows last-active doc as dismissable context chip', done: false },
  { id: 'streaming', label: 'Streaming conversation shows pulsing indicator in sidebar', done: false },
]

function ChatComposerSurface({
  threadId,
  draft,
  onDraftChange,
  surfaceLabel,
  welcomeVariant,
  onMove,
  moveLabel,
  testId,
  contextDoc,
  onFirstSend,
}: {
  threadId: string
  draft: { text: string; references: DocumentReference[]; images: ChatImage[] }
  onDraftChange: (draft: { text: string; references: DocumentReference[]; images: ChatImage[] }) => void
  surfaceLabel: string
  welcomeVariant: 'compact' | 'comfortable'
  onMove: () => void
  moveLabel: string
  testId: string
  contextDoc?: { id: string; title: string } | null
  /** Called once when the first message is sent - clears the "one empty chat" lock. */
  onFirstSend?: () => void
}) {
  const [messages, setMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([])
  const [contextDismissed, setContextDismissed] = useState(false)

  const handleSend = useCallback(() => {
    const text = draft.text.trim()
    if (!text) return
    const isFirst = messages.length === 0
    setMessages((prev) => [
      ...prev,
      { role: 'user', content: text },
      {
        role: 'assistant',
        content: `[Prototype mock reply for "${getConversationTitle(threadId)}"]`,
      },
    ])
    onDraftChange({ text: '', references: [], images: [] })
    if (isFirst) onFirstSend?.()
  }, [threadId, draft.text, messages.length, onDraftChange, onFirstSend])

  const isChatFirstHome = messages.length === 0
  const showContext = isChatFirstHome && contextDoc && !contextDismissed

  const contextNode = showContext ? (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <span className="shrink-0">Context:</span>
      <span className="flex items-center gap-1 rounded-md border bg-muted/60 px-2 py-1">
        <FileText className="h-3 w-3 shrink-0 text-primary/70" />
        <span className="max-w-[180px] truncate">{contextDoc.title}</span>
        <button
          type="button"
          onClick={() => setContextDismissed(true)}
          className="ml-0.5 rounded p-0.5 hover:bg-muted"
          aria-label="Remove context"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </span>
    </div>
  ) : null

  const composerInput = (
    <ContentEditableChatInput
      value={draft.text}
      references={draft.references}
      onChange={(text) => onDraftChange({ ...draft, text })}
      onReferencesChange={(references) => onDraftChange({ ...draft, references })}
      onImagesChange={(images) => onDraftChange({ ...draft, images })}
      onSend={handleSend}
      placeholder="How can I help you today?"
      showToolbar
      data-testid={`${testId}-composer`}
      className={welcomeVariant === 'comfortable' ? 'min-h-[180px] text-base' : 'min-h-[140px] text-sm'}
    />
  )

  return (
    <div className="flex h-full flex-col" data-testid={testId}>
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{surfaceLabel}</p>
          <h2 className="truncate text-sm font-semibold">{getConversationTitle(threadId)}</h2>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onMove}>{moveLabel}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        {isChatFirstHome ? (
          <ChatFirstHome
            userName="Rowan"
            variant={welcomeVariant}
            data-testid={`${testId}-welcome`}
            contextNode={contextNode}
            composer={composerInput}
          />
        ) : (
          <div className="space-y-3 p-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={cn(
                  'max-w-[85%] rounded-lg px-3 py-2 text-sm',
                  msg.role === 'user'
                    ? 'ml-auto bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground',
                )}
              >
                {msg.content}
              </div>
            ))}
            {draft.images.length > 0 && (
              <p className="text-xs text-green-600">
                {draft.images.length} pasted image(s) staged in draft
              </p>
            )}
          </div>
        )}
      </div>

      {!isChatFirstHome && (
        <div className="border-t p-3">
          {composerInput}
        </div>
      )}
    </div>
  )
}

export function AgentsLayoutTest() {
  const registry = useChatSurfaceRegistry()
  const [searchQuery, setSearchQuery] = useState('')
  const [showDocTab, setShowDocTab] = useState(true)
  const [qaItems, setQaItems] = useState(INITIAL_QA)
  const [tabContextMenu, setTabContextMenu] = useState<{
    x: number
    y: number
    tab: EditorChatTab
  } | null>(null)
  const [listContextMenu, setListContextMenu] = useState<{
    x: number
    y: number
    conv: MockConversation
  } | null>(null)
  const [renamingConvId, setRenamingConvId] = useState<string | null>(null)
  const [renamingValue, setRenamingValue] = useState('')
  const [convRenames, setConvRenames] = useState<Record<string, string>>({})
  const [convContextDocs, setConvContextDocs] = useState<Record<string, { id: string; title: string }>>({})
  const [lastFocusedDocTitle, setLastFocusedDocTitle] = useState<string>(MOCK_DOC_TAB.title)
  const renameInputRef = useRef<HTMLInputElement>(null)

  const markQa = useCallback((id: string) => {
    setQaItems((prev) => prev.map((item) => (item.id === id ? { ...item, done: true } : item)))
  }, [])

  const getDisplayTitle = useCallback(
    (conv: MockConversation) => convRenames[conv.id] ?? conv.title,
    [convRenames],
  )

  const startRename = useCallback(
    (conv: MockConversation) => {
      setRenamingConvId(conv.id)
      setRenamingValue(convRenames[conv.id] ?? conv.title)
      setListContextMenu(null)
      setTimeout(() => renameInputRef.current?.select(), 0)
    },
    [convRenames],
  )

  const commitRename = useCallback(() => {
    if (renamingConvId && renamingValue.trim()) {
      const newTitle = renamingValue.trim()
      setConvRenames((prev) => ({ ...prev, [renamingConvId]: newTitle }))
      registry.updateTabTitle(renamingConvId, newTitle)
      markQa('rename')
    }
    setRenamingConvId(null)
  }, [markQa, registry, renamingConvId, renamingValue])

  const handleCreateNewChat = useCallback(() => {
    const threadId = registry.createNewChat()
    // Only attach context doc if a genuinely new chat was created (not a focus of existing)
    if (registry.lastAction !== 'Focused existing new chat (no duplicate)' && lastFocusedDocTitle) {
      setConvContextDocs((prev) => ({
        ...prev,
        [threadId]: { id: MOCK_DOC_TAB.id, title: lastFocusedDocTitle },
      }))
      markQa('doc-context')
    }
    if (registry.lastAction === 'Focused existing new chat (no duplicate)') {
      markQa('no-double-new')
    }
    markQa('cmd-n')
    return threadId
  }, [lastFocusedDocTitle, markQa, registry])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault()
        handleCreateNewChat()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [handleCreateNewChat])

  const filteredConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    // Chats sidebar: agent_chat only. Flow outputs triage through Inbox (Open Decision #1 resolved).
    return MOCK_CONVERSATIONS.filter((c) => {
      const isChat = c.kind === 'agent_chat'
      const matchesSearch = !q || c.title.toLowerCase().includes(q) || c.preview?.toLowerCase().includes(q)
      return isChat && matchesSearch
    })
  }, [searchQuery])

  const grouped = useMemo(
    () => groupConversationsByDay(filteredConversations),
    [filteredConversations],
  )

  const handleOpenConversation = useCallback(
    (id: string, options?: OpenConversationOptions) => {
      registry.openConversation(id, options)
      markQa('no-dup')
      if (options?.openInNewTab) markQa('mod-click')
      if (registry.openInEditor && !options?.forceAgentPanel) markQa('editor-tab')
      if (!registry.openInEditor || options?.forceAgentPanel) markQa('dual')
    },
    [markQa, registry],
  )

  const activeMainView = useMemo(() => {
    if (registry.activeEditorTabId) return 'chat'
    if (showDocTab) return 'doc'
    return 'empty'
  }, [registry.activeEditorTabId, showDocTab])

  const renderEditorContent = () => {
    if (registry.activeEditorTabId && registry.activeEditorThreadId) {
      const convId = registry.activeEditorThreadId
      const draft = registry.getDraft(convId)
      return (
        <ChatComposerSurface
          threadId={convId}
          draft={draft}
          onDraftChange={(d) => {
            registry.setDraft(convId, d)
            if (d.text.includes('move-test')) markQa('move-draft')
          }}
          surfaceLabel="Editor tab"
          welcomeVariant="comfortable"
          onMove={() => {
            registry.moveToAgentPanel(convId)
            markQa('move-draft')
          }}
          moveLabel="Move to agent panel"
          testId={testIds.agentsLayoutPrototype.editorChat}
          contextDoc={convContextDocs[convId] ?? null}
          onFirstSend={() => {
            registry.markChatStarted(convId)
            markQa('no-double-new')
          }}
        />
      )
    }

    if (showDocTab) {
      return (
        <div
          className="flex h-full flex-col p-6"
          data-testid={testIds.agentsLayoutPrototype.editorDocument}
        >
          <h2 className="text-lg font-semibold">{MOCK_DOC_TAB.title}</h2>
          <p className="mt-4 text-sm text-muted-foreground">
            Mock document tab. Open a chat from the left sidebar to see a chat tab appear
            alongside this document.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Click &quot;New chat&quot; (or Cmd/Ctrl+N) while this doc is in focus - the new chat
            will pick it up as context.
          </p>
        </div>
      )
    }

    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a conversation or document tab
      </div>
    )
  }

  return (
    <div
      className="flex h-screen flex-col bg-background"
      data-testid={testIds.agentsLayoutPrototype.root}
    >
      <header className="flex items-center gap-3 border-b bg-sidebar px-4 py-3">
        <Button variant="ghost" size="sm" asChild>
          <a href="/?test=index" data-testid={testIds.agentsLayoutPrototype.backToPrototypeIndex}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Prototypes
          </a>
        </Button>
        <div>
          <h1 className="text-lg font-semibold">Agents Chat Layout</h1>
          <p className="text-xs text-muted-foreground">
            Cursor-style history + editor tabs + agent panel (mock)
          </p>
        </div>
        {registry.lastAction && (
          <p className="ml-auto max-w-md truncate text-xs text-muted-foreground">
            Last: {registry.lastAction}
          </p>
        )}
      </header>

      <PanelGroup direction="horizontal" className="min-h-0 flex-1">
        {/* Icon bar */}
        <div className="flex w-12 shrink-0 flex-col items-center gap-2 border-r bg-sidebar py-3">
          <button type="button" className="rounded p-2 text-muted-foreground opacity-40" title="Browse (mock)">
            <FileText className="h-4 w-4" />
          </button>
          <button type="button" className="rounded p-2 text-muted-foreground opacity-40" title="Inbox (mock)">
            <Inbox className="h-4 w-4" />
          </button>
          <button type="button" className="rounded p-2 text-muted-foreground opacity-40" title="Flows (mock)">
            <Zap className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="rounded bg-sidebar-accent p-2 text-sidebar-foreground"
            title="Chats"
          >
            <Bot className="h-4 w-4" />
          </button>
        </div>

        {/* Chats sidebar */}
        <Panel defaultSize={22} minSize={16} maxSize={32}>
          <aside
            className="flex h-full flex-col border-r bg-sidebar"
            data-testid={testIds.agentsLayoutPrototype.agentsSidebar}
          >
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-sm font-semibold">Chats</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    data-testid={testIds.agentsLayoutPrototype.agentsPanelMenu}
                  >
                    <MoreVertical className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem
                    onSelect={() => {
                      registry.setOpenInEditor(!registry.openInEditor)
                      markQa('toggle')
                    }}
                  >
                    <span className="flex w-4 shrink-0 items-center justify-center mr-2">
                      {registry.openInEditor ? <Check className="h-3.5 w-3.5" /> : null}
                    </span>
                    Open chats in editor
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="border-b p-2 space-y-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    if (e.target.value) markQa('search')
                  }}
                  placeholder="Search chats..."
                  className="h-8 pl-8 text-sm"
                  data-testid={testIds.agentsLayoutPrototype.agentsSearch}
                />
              </div>
              {/* No kind filter - Chats sidebar shows agent_chat only (Open Decision #1 resolved).
                  Flow outputs triage through Inbox; flows that want a chat create kind='agent_chat'. */}
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={handleCreateNewChat}
                data-testid={testIds.agentsLayoutPrototype.agentsNewChat}
              >
                <Plus className="h-3.5 w-3.5" />
                New chat
              </Button>
            </div>

            <div
              className="flex-1 overflow-auto p-2"
              data-testid={testIds.agentsLayoutPrototype.agentsList}
            >
              {grouped.map((group) => (
                <div key={group.label} className="mb-3">
                  <p className="mb-1 px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </p>
                  <div className="space-y-1">
                    {group.items.map((conv) => {
                      const binding = registry.getBinding(conv.id)
                      const isActive =
                        (binding?.surface === 'editor' &&
                          binding.tabId === registry.activeEditorTabId) ||
                        (binding?.surface === 'agent-panel' &&
                          registry.agentPanelThreadId === conv.id)
                      const isRenaming = renamingConvId === conv.id

                      return (
                        <div key={conv.id} className="group relative">
                          <button
                            type="button"
                            onClick={(e) =>
                              handleOpenConversation(conv.id, {
                                openInNewTab: isModClick(e),
                                forceEditor: isModClick(e),
                              })
                            }
                            onContextMenu={(e) => {
                              e.preventDefault()
                              setListContextMenu({ x: e.clientX, y: e.clientY, conv })
                              markQa('right-click')
                            }}
                            onDoubleClick={(e) => {
                              e.preventDefault()
                              startRename(conv)
                            }}
                            className={cn(
                              'w-full rounded-md border px-3 py-2 text-left transition-colors',
                              isActive
                                ? 'border-primary/40 bg-primary/5'
                                : 'border-border bg-card hover:border-primary/30',
                            )}
                          >
                            <div className="flex items-center gap-1.5">
                              {conv.isStreaming && (
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full bg-primary animate-pulse"
                                  title="AI is responding"
                                  aria-label="AI is responding"
                                />
                              )}
                              {isRenaming ? (
                                <input
                                  ref={renameInputRef}
                                  value={renamingValue}
                                  onChange={(e) => setRenamingValue(e.target.value)}
                                  onBlur={commitRename}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitRename()
                                    if (e.key === 'Escape') setRenamingConvId(null)
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="min-w-0 flex-1 rounded border bg-background px-1 text-sm outline-none focus:ring-1 focus:ring-primary"
                                />
                              ) : (
                                <p className="min-w-0 flex-1 truncate text-sm font-medium">{getDisplayTitle(conv)}</p>
                              )}
                              {conv.kind !== 'agent_chat' && (
                                <span className={cn(
                                  'shrink-0 rounded border px-1 py-0.5 text-[9px] font-medium',
                                  THREAD_KIND_META[conv.kind].chipClass,
                                )}>
                                  {THREAD_KIND_META[conv.kind].label}
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {conv.message_count} messages &middot; {formatConversationDate(conv.updated_at)}
                            </p>
                            {binding && (
                              <p className="mt-0.5 text-[10px] text-primary">
                                Open in {binding.surface === 'editor' ? 'editor' : 'agent panel'}
                              </p>
                            )}
                          </button>
                          <button
                            type="button"
                            title="Open in agent panel"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleOpenConversation(conv.id, { forceAgentPanel: true })
                            }}
                            className="absolute right-1 top-1 hidden rounded p-1 text-muted-foreground hover:bg-muted group-hover:block"
                          >
                            <PanelRight className="h-3 w-3" />
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </Panel>

        <PanelResizeHandle className="w-1 bg-border hover:bg-primary/30" />

        {/* Main editor area */}
        <Panel defaultSize={registry.agentPanelExpanded ? 48 : 70} minSize={35}>
          <main className="flex h-full min-w-0 flex-col">
            <div
              className="flex items-center gap-0.5 overflow-x-auto border-b bg-muted/40 px-2 pt-1 [scrollbar-width:none]"
              data-testid={testIds.agentsLayoutPrototype.editorTabs}
            >
              {showDocTab && (
                <button
                  type="button"
                  onClick={() => {
                    registry.setActiveEditorTabId(null)
                    setLastFocusedDocTitle(MOCK_DOC_TAB.title)
                  }}
                  className={cn(
                    'flex max-w-[200px] shrink-0 items-center gap-1.5 rounded-t-md border border-b-0 px-3 py-1.5 text-xs',
                    activeMainView === 'doc'
                      ? 'border-border bg-background text-foreground'
                      : 'border-transparent text-muted-foreground hover:bg-background/60',
                  )}
                >
                  <FileText className="h-3 w-3 shrink-0" />
                  <span className="truncate">{MOCK_DOC_TAB.title}</span>
                </button>
              )}

              {registry.editorTabs.map((tab) => (
                <div
                  key={tab.tabId}
                  className={cn(
                    'group/tab flex max-w-[240px] shrink-0 items-center rounded-t-md border border-b-0 text-xs',
                    registry.activeEditorTabId === tab.tabId
                      ? 'border-border bg-background text-foreground'
                      : 'border-transparent text-muted-foreground hover:bg-background/60',
                  )}
                >
                  <button
                    type="button"
                    onClick={() => registry.setActiveEditorTabId(tab.tabId)}
                    onDoubleClick={(e) => {
                      e.preventDefault()
                      registry.togglePinEditorTab(tab.tabId)
                      markQa('pin')
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      setTabContextMenu({ x: e.clientX, y: e.clientY, tab })
                    }}
                    className="flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1.5"
                    title={tab.isPinned ? tab.title : `${tab.title} (double-click to pin)`}
                  >
                    {tab.isPinned ? (
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-primary/15">
                        <MessageSquare className="h-3 w-3 text-primary" />
                      </span>
                    ) : (
                      <MessageSquare className="h-3 w-3 shrink-0 text-blue-500" />
                    )}
                    <span
                      className={cn(
                        'truncate',
                        !tab.isPinned && 'italic text-muted-foreground/80',
                      )}
                    >
                      {tab.title}
                    </span>
                    {tab.isPinned && (
                      <Pin className="h-2.5 w-2.5 shrink-0 text-primary/70" aria-hidden />
                    )}
                  </button>
                  <button
                    type="button"
                    aria-label={`Close ${tab.title}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      registry.closeEditorTab(tab.tabId)
                    }}
                    className="mr-1 rounded p-0.5 opacity-0 hover:bg-muted group-hover/tab:opacity-100"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>

            <div
              className="min-h-0 flex-1 overflow-hidden bg-background"
              data-testid={testIds.agentsLayoutPrototype.editorContent}
            >
              {renderEditorContent()}
            </div>
          </main>
        </Panel>

        {registry.agentPanelExpanded && (
          <>
            <PanelResizeHandle className="w-1 bg-border hover:bg-primary/30" />
            <Panel defaultSize={30} minSize={20} maxSize={45}>
              <aside
                className="flex h-full flex-col border-l bg-background"
                data-testid={testIds.agentsLayoutPrototype.agentPanel}
              >
                {registry.agentPanelThreadId ? (
                  <ChatComposerSurface
                    threadId={registry.agentPanelThreadId}
                    draft={registry.getDraft(registry.agentPanelThreadId)}
                    onDraftChange={(d) =>
                      registry.setDraft(registry.agentPanelThreadId!, d)
                    }
                    surfaceLabel="Agent panel"
                    welcomeVariant="compact"
                    onMove={() =>
                      registry.moveToEditorTab(registry.agentPanelThreadId!)
                    }
                    moveLabel="Move to editor tab"
                    testId={testIds.agentsLayoutPrototype.agentPanelChat}
                    contextDoc={convContextDocs[registry.agentPanelThreadId] ?? null}
                    onFirstSend={() => {
                      registry.markChatStarted(registry.agentPanelThreadId!)
                      markQa('no-double-new')
                    }}
                  />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground">
                    <Bot className="h-8 w-8 opacity-40" />
                    <p>No chat in agent panel.</p>
                    <p className="text-xs">
                      Turn off &quot;Open chats in editor&quot; or use the panel icon on a list item.
                    </p>
                  </div>
                )}
              </aside>
            </Panel>
          </>
        )}
      </PanelGroup>

      {/* QA checklist */}
      <section
        className="border-t bg-muted/20 px-4 py-3"
        data-testid={testIds.agentsLayoutPrototype.qaChecklist}
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-start gap-4">
          <div className="min-w-[240px]">
            <h3 className="text-sm font-semibold">Prototype QA checklist</h3>
            <p className="text-xs text-muted-foreground">
              Try each scenario; click to mark done.
            </p>
          </div>
          <ul className="flex flex-1 flex-wrap gap-2">
            {qaItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => markQa(item.id)}
                  className={cn(
                    'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-left text-xs transition-colors',
                    item.done
                      ? 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400'
                      : 'border-border bg-background hover:border-primary/40',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border',
                      item.done && 'border-green-500 bg-green-500 text-white',
                    )}
                  >
                    {item.done ? <Check className="h-2.5 w-2.5" /> : null}
                  </span>
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
          <Button
            variant="outline"
            size="sm"
            onClick={() => registry.setAgentPanelExpanded(!registry.agentPanelExpanded)}
          >
            {registry.agentPanelExpanded ? 'Hide' : 'Show'} agent panel
          </Button>
        </div>
      </section>

      {/* Tab context menu */}
      {tabContextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setTabContextMenu(null)} />
          <div
            className="fixed z-50 min-w-[180px] rounded-md border bg-popover p-1 shadow-md"
            style={{ left: tabContextMenu.x, top: tabContextMenu.y }}
          >
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                registry.togglePinEditorTab(tabContextMenu.tab.tabId)
                markQa('pin')
                setTabContextMenu(null)
              }}
            >
              {tabContextMenu.tab.isPinned ? 'Unpin tab' : 'Pin tab'}
            </button>
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                registry.moveToAgentPanel(tabContextMenu.tab.threadId)
                markQa('move-draft')
                setTabContextMenu(null)
              }}
            >
              Move to agent panel
            </button>
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                registry.closeEditorTab(tabContextMenu.tab.tabId)
                setTabContextMenu(null)
              }}
            >
              Close tab
            </button>
          </div>
        </>
      )}

      {/* List item context menu */}
      {listContextMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setListContextMenu(null)} />
          <div
            className="fixed z-50 min-w-[200px] rounded-md border bg-popover p-1 shadow-md"
            style={{ left: listContextMenu.x, top: listContextMenu.y }}
          >
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                handleOpenConversation(listContextMenu.conv.id, { forceEditor: true })
                setListContextMenu(null)
              }}
            >
              Open in editor tab
            </button>
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                handleOpenConversation(listContextMenu.conv.id, {
                  openInNewTab: true,
                  forceEditor: true,
                })
                setListContextMenu(null)
              }}
            >
              Open in new editor tab
            </button>
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => {
                handleOpenConversation(listContextMenu.conv.id, { forceAgentPanel: true })
                setListContextMenu(null)
              }}
            >
              Open in agent panel
            </button>
            <div className="my-1 border-t" />
            <button
              type="button"
              className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              onClick={() => startRename(listContextMenu.conv)}
            >
              Rename
            </button>
          </div>
        </>
      )}
    </div>
  )
}
